import React, { useState, useRef, useCallback } from 'react';
import { wikiAPI, fileUtils } from '../../api/api';
import { useAuth } from '../../hooks/useAuth'; // Импортируем useAuth
import './FileUploadModal.css';

const FileUploadModal = ({ documentId, onClose, onFilesUploaded }) => {
  const { user } = useAuth(); // Получаем пользователя
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const filesArray = fileUtils.fileListToArray(files);
    setSelectedFiles(prev => [...prev, ...filesArray]);
    setError(null);
  };

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const relatedTarget = event.relatedTarget;
    const currentTarget = event.currentTarget;
    
    if (!currentTarget.contains(relatedTarget)) {
      setDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    const filesArray = fileUtils.fileListToArray(files);
    setSelectedFiles(prev => [...prev, ...filesArray]);
    setError(null);
  }, []);

  const handleFileUpload = async () => {
    if (!documentId) {
      setError('Документ не найден');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Пожалуйста, выберите файлы для загрузки');
      return;
    }

    // Проверяем, что пользователь авторизован
    if (!user || !user.id) {
      setError('Пользователь не авторизован');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const maxSize = 10 * 1024 * 1024;
      const oversizedFiles = selectedFiles.filter(file => file.size > maxSize);
      
      if (oversizedFiles.length > 0) {
        setError(`Некоторые файлы превышают максимальный размер 10MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
        setUploading(false);
        return;
      }


      
      const result = await wikiAPI.uploadFiles(documentId, selectedFiles);
      
      if (result.success) {
        if (onFilesUploaded) {
          onFilesUploaded(result.files);
        }
        
        onClose();
        alert(`Успешно загружено ${result.files.length} файлов`);
      } else {
        throw new Error(result.error || 'Ошибка загрузки файлов');
      }
    } catch (error) {
     
      setError(`Ошибка загрузки файлов: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };


  return (
    <div className="modal-overlay file-upload-modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Загрузка файлов</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-content">
          <div 
            className={`file-upload-area ${dragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
              id="file-upload-input"
              className="file-upload-input"
            />
              <label 
                htmlFor="file-upload-input" 
                className={`file-upload-label ${uploading ? 'disabled' : ''}`}
              >
              <div className="drag-drop-content">
                <i className="fas fa-cloud-upload-alt file-upload-icon"></i>
                <span className="file-upload-text">
                  {dragOver ? 'Отпустите файлы здесь' : 'Перетащите файлы сюда или нажмите для выбора'}
                </span>
                <small className="file-upload-subtext">Максимальный размер файла: 10MB</small>
                
                {dragOver && (
                  <div className="drag-drop-overlay">
                    <i className="fas fa-file-upload drag-drop-icon"></i>
                    <span className="drag-drop-text">Отпустите для загрузки</span>
                  </div>
                )}
              </div>
            </label>
          </div>

          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4 className="selected-files-title">Выбранные файлы ({selectedFiles.length}):</h4>
              <div className="files-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <div className="file-info">
                      <i 
                        className={`fas fa-file file-icon ${fileUtils.getFileType(file.type, file.name)}`}
                      ></i>
                      <div className="file-details">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          {fileUtils.formatFileSize(file.size)}
                        </span>
                      </div>
                    </div>
                    <button 
                      className="remove-file-btn" 
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-error file-upload-error">
              <i className="fas fa-exclamation-triangle file-upload-error-icon"></i>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-primary file-upload-submit-btn"
            onClick={handleFileUpload}
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading ? (
              <>
                <div className="spinner-small file-upload-spinner"></div>
                Загрузка...
              </>
            ) : (
              <>
                <i className="fas fa-upload file-upload-btn-icon"></i>
                Загрузить файлы ({selectedFiles.length})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
